/**
 * P-5 日報設定 — 項目定義 (`/settings/daily-report/fields`、§14-2)。
 * テナントが使う日報項目の追加・ラベル・型・計算式・可視性クラスを管理する。
 * バックエンド: apps/api/src/routes/daily-reports.ts GET/PUT /daily-reports/config/fields。
 * 設計: docs/design/v05-daily-report-detail.md §3-4 / §14-2 / §14-5-5 / §14-5-6
 *
 * 可視性クラスは P-8 (`/settings/daily-report/visibility-classes`) で管理する。
 * プレビュー（§14-5-6）: 新規のレンダリングロジックを作らず、P-3 が実際に使う
 * `DailyReportFieldRow.tsx` をそのまま再利用する (§8-134型のミラーずれを避けるため)。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil } from 'lucide-react';
import { ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  fetchDailyReportConfigFields, saveDailyReportConfigField, fetchDailyReportVisibilityClasses,
  MIN_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT as MIN_ATTACHMENT_REQUIRED_COUNT,
  MAX_DAILY_REPORT_ATTACHMENT_REQUIRED_COUNT as MAX_ATTACHMENT_REQUIRED_COUNT,
  type DailyReportConfigField, type DailyReportFieldUpsertBody, type DailyReportValueKind, type DailyReportField,
} from '@/lib/daily-report-api';
import { DrExprEditor, buildDrAst, decomposeDrAst, type DrExpr } from './DailyReportAstEditor';
import { DailyReportFieldRow } from '../daily-reports/DailyReportFieldRow';

const VALUE_KIND_LABELS: Record<DailyReportValueKind, string> = {
  text: 'テキスト', number: '数値', money: '金額', select: '選択式', check: 'チェック',
  line_items: '明細行', attachment: '添付', computed: '計算欄 (自動計算)',
};
const VALUE_KINDS = Object.keys(VALUE_KIND_LABELS) as DailyReportValueKind[];
/** カタログ連携の売上項目は税区分がカタログで固定 (バックエンド lib/consumption-tax.ts の CATALOG_TAX_BASIS と同じ)。 */
const CATALOG_TAX_BASIS: Record<string, 'tax_excluded' | 'tax_included'> = {
  'sales.total_excl_tax': 'tax_excluded',
  'sales.total_incl_tax': 'tax_included',
};
const CARRYOVER_ELIGIBLE_KINDS: DailyReportValueKind[] = ['number', 'money', 'text', 'select', 'check'];
const ATTACHMENT_KINDS = ['journal_timeslot', 'journal_menu', 'handwritten_report', 'delivery_slip', 'cash_count', 'other'] as const;
const ATTACHMENT_KIND_LABELS: Record<string, string> = {
  journal_timeslot: '食券機ジャーナル（時間帯別）', journal_menu: '食券機ジャーナル（メニュー別）',
  handwritten_report: '手書き日報', delivery_slip: '納品書', cash_count: '現金カウント', other: 'その他',
};

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

type SelectOption = { value: string; label: string };
type LineItemColumnDraft = { key: string; label: string; type: 'text' | 'number' | 'date'; required: boolean };

type Draft = {
  id: string | null;
  field_code: string;
  label: string;
  value_kind: DailyReportValueKind;
  section_label: string;
  section_order: string;
  sort_order: string;
  status: 'active' | 'archived';
  // kind別の構造化編集用ローカル state
  selectOptions: SelectOption[];
  lineItemKind: 'denomination' | 'general';
  lineItemColumns: LineItemColumnDraft[];
  lineItemAmountColumnKey: string;
  attachmentKind: string;
  attachmentRequiredCount: string;
  /** 2026-09-09 (user Q1): OCR成功時にどの数値/金額項目へ自動反映するか。未設定なら反映しない。 */
  ocrTargetFieldId: string;
  /** 金額項目の税区分 (Full 限定、docs/design/v05-consumption-tax-rate-and-target-tax-basis.md §3-5)。 */
  taxBasis: 'tax_excluded' | 'tax_included';
  /** カタログ連携の売上項目なら、カタログで決まる税区分 (画面では変更させない)。 */
  catalogTaxBasis: 'tax_excluded' | 'tax_included' | null;
  /** 税込のとき「うち軽減税率対象の売上(税込)」を入力する金額項目。 */
  reducedRatePortionFieldId: string;
  computedExpr: DrExpr | null;
  computedRawFallback: string | null; // decompose不能な既存式はJSON文字列のまま保持し、変更しなければ壊さない
  carryoverSourceFieldId: string;
  visibilityClassId: string;
  validationJson: string;
};

const emptyDraft = (sectionOrder: number, sortOrder: number): Draft => ({
  id: null, field_code: '', label: '', value_kind: 'text',
  section_label: '', section_order: String(sectionOrder), sort_order: String(sortOrder), status: 'active',
  selectOptions: [], lineItemKind: 'denomination', lineItemColumns: [], lineItemAmountColumnKey: '',
  attachmentKind: 'other', attachmentRequiredCount: '1', ocrTargetFieldId: '', taxBasis: 'tax_excluded', catalogTaxBasis: null, reducedRatePortionFieldId: '',
  computedExpr: null, computedRawFallback: null,
  carryoverSourceFieldId: '', visibilityClassId: '', validationJson: '',
});

const draftFromField = (f: DailyReportConfigField): Draft => {
  const raw = f.field_options_json as Record<string, unknown> | null;
  // ⚠️ select の field_options_json は `{options:[...]}` ではなく生配列そのもの
  // (DailyReportFieldRow.tsx parseFieldOptions と揃える、実データで実測して判明: string[] または
  // {value,label}[] のいずれか)。
  const selectOptions: SelectOption[] = f.value_kind === 'select' && Array.isArray(f.field_options_json)
    ? (f.field_options_json as unknown[]).map((o) => (typeof o === 'string' ? { value: o, label: o } : (o as SelectOption)))
    : [];
  const lineItemKind = (raw as { line_item_kind?: string })?.line_item_kind === 'general' ? 'general' : 'denomination';
  const lineItemColumns: LineItemColumnDraft[] = f.value_kind === 'line_items' && Array.isArray((raw as { columns?: unknown })?.columns)
    ? (raw as { columns: LineItemColumnDraft[] }).columns.map((c) => ({ ...c, required: !!c.required }))
    : [];
  const decomposed = f.value_kind === 'computed' ? decomposeDrAst(f.expression_ast_json) : null;
  return {
    id: f.field_definition_id, field_code: f.field_code, label: f.label ?? '', value_kind: f.value_kind,
    section_label: f.section_label ?? '', section_order: String(f.section_order), sort_order: String(f.sort_order),
    status: f.status,
    selectOptions, lineItemKind, lineItemColumns,
    lineItemAmountColumnKey: (raw as { amount_column_key?: string })?.amount_column_key ?? '',
    attachmentKind: (raw as { attachment_kind?: string })?.attachment_kind ?? 'other',
    attachmentRequiredCount: String((raw as { required_count?: number })?.required_count ?? 1),
    ocrTargetFieldId: (raw as { ocr_target_field_id?: string })?.ocr_target_field_id ?? '',
    taxBasis: (raw as { tax_basis?: string })?.tax_basis === 'tax_included' ? 'tax_included' : 'tax_excluded',
    catalogTaxBasis: f.catalog_field_key ? CATALOG_TAX_BASIS[f.catalog_field_key] ?? null : null,
    reducedRatePortionFieldId: (raw as { reduced_rate_portion_field_id?: string })?.reduced_rate_portion_field_id ?? '',
    computedExpr: decomposed,
    computedRawFallback: f.value_kind === 'computed' && !decomposed && f.expression_ast_json ? JSON.stringify(f.expression_ast_json) : null,
    carryoverSourceFieldId: f.carryover_source_field_id ?? '', visibilityClassId: f.visibility_class_id ?? '',
    validationJson: f.validation_json ? JSON.stringify(f.validation_json) : '',
  };
};

/** §14-5-6: フォームの現在の入力値から仮の DailyReportField を組み立てる (API呼び出し不要)。
 *  `visibility:'required'`/`display_level:'full'` を仮定する — オーサー視点のレイアウト確認が
 *  目的で、可視性クラスによる非表示・存在のみ表示のシミュレーションはスコープ外 (§14-5-6 参照)。 */
const buildPreviewField = (d: Draft): DailyReportField => ({
  field_definition_id: d.id ?? '__preview__',
  field_code: d.field_code || 'field_code',
  catalog_field_key: null,
  label: d.label || d.field_code || '(ラベル未設定)',
  value_kind: d.value_kind,
  field_options_json: (() => {
    const json = buildFieldOptionsJson(d);
    return json === null ? null : JSON.stringify(json);
  })(),
  validation_json: null,
  section_label: d.section_label || null,
  section_order: Number(d.section_order) || 0,
  sort_order: Number(d.sort_order) || 0,
  visibility: 'required',
  condition_set_id: null,
  display_level: 'full',
  value: null,
  source: d.value_kind === 'computed' ? 'computed' : null,
  is_not_applicable: false,
});

/** §14-5-6: P-3 の入力画面でどう見えるかのプレビュー。attachment/line_items は
 *  `DailyReportFieldRow` の対象外 (P-3 では専用コンポーネントが描画する) のため、
 *  誤って汎用テキスト欄として見せないよう案内文に留める。 */
function FieldPreview({ draft }: { draft: Draft }) {
  if (draft.value_kind === 'attachment' || draft.value_kind === 'line_items') {
    const requiredCount = Number(draft.attachmentRequiredCount) || 1;
    return (
      <p className="text-xs text-text-sub">
        {draft.value_kind === 'attachment' ? '証跡（写真添付）' : '明細行'}
        は入力画面では専用のUI（{draft.value_kind === 'attachment' ? '撮影・OCR確認' : 'カード形式の行編集'}）で表示されるため、このプレビューでは再現していません。
        {draft.value_kind === 'attachment' && requiredCount > 1 && ` 必要枚数: ${requiredCount}枚`}
      </p>
    );
  }
  // design-expertレビュー HIGH是正: `bg-white` だと本物の編集可能な入力欄と見分けがつかず
  // 「今すぐ打ち込める」と誤認されるリスクがあった。既存の carryover/computed と同じ
  // 「淡色背景で非活性を示す」語彙 (DailyReportFieldRow.tsx の wrapperCls/isComputed 分岐参照) に揃え、
  // 右上にプレビューである旨のバッジを重ねる。
  return (
    <div className="relative rounded border border-surface-border bg-surface-sunken p-3 pt-6">
      <span className="absolute top-1.5 right-2 text-[11px] text-text-sub pointer-events-none select-none">プレビュー（編集不可）</span>
      <DailyReportFieldRow field={buildPreviewField(draft)} disabled onValueChange={() => {}} />
    </div>
  );
}

/** 税込の金額項目が「軽減税率対象額の項目」として参照している項目 → 参照元のラベル。 */
const reducedRateReferrers = (fields: DailyReportConfigField[]): Map<string, string> => {
  const out = new Map<string, string>();
  for (const f of fields) {
    if (f.value_kind !== 'money' || f.status !== 'active') continue;
    const raw = f.field_options_json as { tax_basis?: string; reduced_rate_portion_field_id?: string } | null;
    if (raw?.tax_basis === 'tax_included' && raw.reduced_rate_portion_field_id) {
      out.set(raw.reduced_rate_portion_field_id, f.label || f.field_code);
    }
  }
  return out;
};

const buildFieldOptionsJson = (d: Draft): Record<string, unknown> | unknown[] | null => {
  // ⚠️ 生配列 ({options:[...]}ではない) — DailyReportFieldRow.tsx parseFieldOptions が
  // 生配列前提でパースするため揃える (実データで実測して判明)。
  if (d.value_kind === 'select') return d.selectOptions.filter((o) => o.value.trim() !== '');
  if (d.value_kind === 'line_items') {
    if (d.lineItemKind === 'denomination') return { line_item_kind: 'denomination' };
    return {
      line_item_kind: 'general',
      columns: d.lineItemColumns.filter((c) => c.key.trim() !== ''),
      ...(d.lineItemAmountColumnKey ? { amount_column_key: d.lineItemAmountColumnKey } : {}),
    };
  }
  // 税抜 (既定) は null で保存する (Lite テナントへ tax_basis を送らないため)。
  // カタログ連携項目は税区分を送らない (カタログで決まり、指定すると 400)。
  if (d.value_kind === 'money' && (d.catalogTaxBasis ?? d.taxBasis) === 'tax_included') {
    return {
      ...(d.catalogTaxBasis ? {} : { tax_basis: 'tax_included' }),
      ...(d.reducedRatePortionFieldId ? { reduced_rate_portion_field_id: d.reducedRatePortionFieldId } : {}),
    };
  }
  if (d.value_kind === 'attachment') {
    const requiredCount = Math.min(MAX_ATTACHMENT_REQUIRED_COUNT, Math.max(MIN_ATTACHMENT_REQUIRED_COUNT, Number(d.attachmentRequiredCount) || 1));
    return {
      attachment_kind: d.attachmentKind, required_count: requiredCount,
      ...(d.ocrTargetFieldId ? { ocr_target_field_id: d.ocrTargetFieldId } : {}),
    };
  }
  return null;
};

export function DailyReportFieldsSettingsPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  // 消費税率マスタは Full 限定のため、税区分の設定も Full のみ出す
  const isFull = useAuthStore((s) => s.user?.edition) !== 'lite';

  const { data: fields = [], isLoading, isError } = useQuery<DailyReportConfigField[]>({
    queryKey: ['daily-report-config-fields'], queryFn: fetchDailyReportConfigFields,
  });
  const { data: visibilityClasses = [] } = useQuery({
    queryKey: ['daily-report-visibility-classes'], queryFn: fetchDailyReportVisibilityClasses,
  });
  const activeVisibilityClasses = useMemo(() => visibilityClasses.filter((c) => c.status === 'active'), [visibilityClasses]);

  const otherFields = useMemo(() => fields.filter((f) => f.field_definition_id !== draft?.id), [fields, draft]);
  const lineItemFields = useMemo(() => fields.filter((f) => f.value_kind === 'line_items'), [fields]);
  const carryoverCandidates = useMemo(
    () => otherFields.filter((f) => CARRYOVER_ELIGIBLE_KINDS.includes(f.value_kind)),
    [otherFields],
  );
  // 2026-09-09 (user Q1): OCR結果の反映先は数値・金額項目のみ (バックエンド routes/daily-reports.ts
  // の検証と揃える)。
  const ocrTargetCandidates = useMemo(
    () => otherFields.filter((f) => f.value_kind === 'number' || f.value_kind === 'money'),
    [otherFields],
  );

  const reducedRateCandidates = useMemo(
    () => otherFields.filter((f) => f.value_kind === 'money' && f.status === 'active'),
    [otherFields],
  );
  const referrersByField = useMemo(() => reducedRateReferrers(fields), [fields]);

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.section_order - b.section_order || a.sort_order - b.sort_order),
    [fields],
  );

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const body: DailyReportFieldUpsertBody = {
        ...(d.id ? { field_definition_id: d.id } : {}),
        field_code: d.field_code.trim(),
        label: d.label.trim() || null,
        value_kind: d.value_kind,
        field_options_json: buildFieldOptionsJson(d),
        validation_json: d.validationJson.trim() ? (JSON.parse(d.validationJson) as Record<string, unknown>) : null,
        expression_ast_json: d.value_kind === 'computed'
          ? (d.computedExpr ? buildDrAst(d.computedExpr) : (d.computedRawFallback ? (JSON.parse(d.computedRawFallback) as Record<string, unknown>) : null))
          : null,
        carryover_source_field_id: d.carryoverSourceFieldId || null,
        visibility_class_id: d.visibilityClassId.trim() || null,
        section_label: d.section_label.trim() || null,
        section_order: Number(d.section_order) || 0,
        sort_order: Number(d.sort_order) || 0,
        status: d.status,
      };
      return saveDailyReportConfigField(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['daily-report-config-fields'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = !!draft && draft.field_code.trim().length > 0
    && (draft.value_kind !== 'computed' || !!draft.computedExpr || !!draft.computedRawFallback)
    && (() => { try { if (draft.validationJson.trim()) JSON.parse(draft.validationJson); return true; } catch { return false; } })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">項目定義</h2>
          <p className="text-sm text-text-sub mt-1">日報で使う項目の追加・ラベル・型・計算式を設定します。</p>
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} />}
          onClick={() => setDraft(emptyDraft(fields.length > 0 ? Math.max(...fields.map((f) => f.section_order)) : 0, fields.length))}>
          項目を追加
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text">項目一覧の取得に失敗しました。</p>
      ) : sortedFields.length === 0 ? (
        <EmptyState message="項目がまだありません" description="「項目を追加」から登録してください。" />
      ) : (
        <ul className="space-y-2">
          {sortedFields.map((f) => (
            <li key={f.field_definition_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-body break-words">
                  {f.label || f.field_code}
                  <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border align-middle">{VALUE_KIND_LABELS[f.value_kind]}</span>
                  {f.status === 'archived' && (
                    <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border align-middle">無効化済み</span>
                  )}
                  {isFull && f.value_kind === 'money' && ((f.catalog_field_key ? CATALOG_TAX_BASIS[f.catalog_field_key] : null)
                    ?? (f.field_options_json as { tax_basis?: string } | null)?.tax_basis) === 'tax_included' && (
                    <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border align-middle">税込</span>
                  )}
                </p>
                {isFull && referrersByField.has(f.field_definition_id) && (
                  <p className="text-xs text-text-sub mt-0.5">{referrersByField.get(f.field_definition_id)}の軽減税率対象額として使用中</p>
                )}
                <p className="text-xs text-text-sub mt-0.5">
                  code: {f.field_code} {f.section_label ? `／ セクション: ${f.section_label}` : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />} onClick={() => setDraft(draftFromField(f))}>編集</Button>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.id ? '項目を編集' : '項目を追加'} onClose={() => setDraft(null)} size="lg"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-text-sub mb-1">項目コード (半角英数字・アンダースコア) <span className="text-text-sub">*</span></label>
                <input value={draft.field_code} disabled={!!draft.id} onChange={(e) => setDraft({ ...draft, field_code: e.target.value })}
                  maxLength={100} className={`${inputCls} disabled:opacity-60`} placeholder="例: sales_total" />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">型 (作成後は変更不可)</label>
                <Select value={draft.value_kind} disabled={!!draft.id} onValueChange={(v) => setDraft({ ...draft, value_kind: v as DailyReportValueKind })}>
                  <SelectTrigger aria-label="型" className="disabled:opacity-60"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VALUE_KINDS.map((k) => <SelectItem key={k} value={k}>{VALUE_KIND_LABELS[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">ラベル (画面表示名)</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 売上総額" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm text-text-sub mb-1">セクション名</label>
                <input value={draft.section_label} onChange={(e) => setDraft({ ...draft, section_label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 売上" />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">セクション順</label>
                <input type="number" min={0} max={1000} value={draft.section_order} onChange={(e) => setDraft({ ...draft, section_order: e.target.value })} className={numberInputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">項目順 (セクション内)</label>
                <input type="number" min={0} max={1000} value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className={numberInputCls} />
              </div>
            </div>

            {draft.value_kind === 'select' && (
              <div className="space-y-2 rounded border border-surface-border p-3 bg-surface-sunken">
                <p className="text-xs font-semibold text-text-sub">選択肢</p>
                {draft.selectOptions.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={o.value} onChange={(e) => {
                      const next = [...draft.selectOptions]; next[i] = { ...o, value: e.target.value };
                      setDraft({ ...draft, selectOptions: next });
                    }} placeholder="値" className={`${inputCls} flex-1`} />
                    <input value={o.label} onChange={(e) => {
                      const next = [...draft.selectOptions]; next[i] = { ...o, label: e.target.value };
                      setDraft({ ...draft, selectOptions: next });
                    }} placeholder="表示ラベル" className={`${inputCls} flex-1`} />
                    <button type="button" aria-label="選択肢を削除" onClick={() => setDraft({ ...draft, selectOptions: draft.selectOptions.filter((_, j) => j !== i) })}
                      className="w-11 h-11 shrink-0 inline-flex items-center justify-center text-text-sub hover:text-danger-text focus-ring">✕</button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />}
                  onClick={() => setDraft({ ...draft, selectOptions: [...draft.selectOptions, { value: '', label: '' }] })}>選択肢を追加</Button>
              </div>
            )}

            {draft.value_kind === 'line_items' && (
              <div className="space-y-2 rounded border border-surface-border p-3 bg-surface-sunken">
                <p className="text-xs font-semibold text-text-sub">明細行の種類</p>
                <Select value={draft.lineItemKind} onValueChange={(v) => setDraft({ ...draft, lineItemKind: v as 'denomination' | 'general' })}>
                  <SelectTrigger aria-label="明細行の種類"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="denomination">金種明細 (現金カウント)</SelectItem>
                    <SelectItem value="general">領収書明細 (自由な列構成)</SelectItem>
                  </SelectContent>
                </Select>
                {draft.lineItemKind === 'general' && (
                  <div className="space-y-2 pt-2 border-t border-surface-line">
                    <p className="text-xs text-text-sub">列 (key は半角英数字、label は画面表示名)</p>
                    {draft.lineItemColumns.map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5 flex-wrap">
                        <input value={c.key} onChange={(e) => {
                          const next = [...draft.lineItemColumns]; next[i] = { ...c, key: e.target.value };
                          setDraft({ ...draft, lineItemColumns: next });
                        }} placeholder="key" className={`${inputCls} w-28`} />
                        <input value={c.label} onChange={(e) => {
                          const next = [...draft.lineItemColumns]; next[i] = { ...c, label: e.target.value };
                          setDraft({ ...draft, lineItemColumns: next });
                        }} placeholder="表示ラベル" className={`${inputCls} flex-1 min-w-0`} />
                        <Select value={c.type} onValueChange={(v) => {
                          const next = [...draft.lineItemColumns]; next[i] = { ...c, type: v as 'text' | 'number' | 'date' };
                          setDraft({ ...draft, lineItemColumns: next });
                        }}>
                          <SelectTrigger className="w-24 shrink-0" aria-label="列の型"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">文字</SelectItem>
                            <SelectItem value="number">数値</SelectItem>
                            <SelectItem value="date">日付</SelectItem>
                          </SelectContent>
                        </Select>
                        <button type="button" aria-label="列を削除" onClick={() => setDraft({ ...draft, lineItemColumns: draft.lineItemColumns.filter((_, j) => j !== i) })}
                          className="w-11 h-11 shrink-0 inline-flex items-center justify-center text-text-sub hover:text-danger-text focus-ring">✕</button>
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />}
                      onClick={() => setDraft({ ...draft, lineItemColumns: [...draft.lineItemColumns, { key: '', label: '', type: 'text', required: false }] })}>列を追加</Button>
                    <div>
                      <label className="block text-sm text-text-sub mb-1 mt-2">小計に使う列の key (任意、数値列のみ)</label>
                      <input value={draft.lineItemAmountColumnKey} onChange={(e) => setDraft({ ...draft, lineItemAmountColumnKey: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {draft.value_kind === 'attachment' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-text-sub mb-1">添付の種類</label>
                  <Select value={draft.attachmentKind} onValueChange={(v) => setDraft({ ...draft, attachmentKind: v })}>
                    <SelectTrigger aria-label="添付の種類"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ATTACHMENT_KINDS.map((k) => <SelectItem key={k} value={k}>{ATTACHMENT_KIND_LABELS[k]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-text-sub mb-1">必要枚数（最低）</label>
                  <input type="number" min={MIN_ATTACHMENT_REQUIRED_COUNT} max={MAX_ATTACHMENT_REQUIRED_COUNT}
                    value={draft.attachmentRequiredCount}
                    onChange={(e) => {
                      const v = e.target.value;
                      // バックエンド検証と揃える: OCR自動反映は1枚構成の項目にのみ許可 (複数枚だと
                      // 各アップロードの結果が合算されず「最後の1枚分」で上書きされてしまうため)。
                      // 2枚以上に変更したら既存の反映先設定を無言では残さずクリアする。
                      setDraft({ ...draft, attachmentRequiredCount: v, ocrTargetFieldId: Number(v) === 1 ? draft.ocrTargetFieldId : '' });
                    }}
                    className={numberInputCls} />
                  <p className="text-xs text-text-sub mt-1">
                    提出時にこの枚数以上添付されているかを確認します（多く添付しても提出は妨げません）。
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-text-sub mb-1">OCR結果の反映先 (任意)</label>
                  <Select
                    value={draft.ocrTargetFieldId || '__none__'} disabled={Number(draft.attachmentRequiredCount) !== 1}
                    onValueChange={(v) => setDraft({ ...draft, ocrTargetFieldId: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger aria-label="OCR結果の反映先"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">指定なし (自動反映しない)</SelectItem>
                      {ocrTargetCandidates.map((f) => <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.label ?? f.field_code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-sub mt-1">
                    {Number(draft.attachmentRequiredCount) !== 1
                      ? '必要枚数が1枚のときのみ設定できます（複数枚だと各アップロードの結果が合算されず、最後の1枚分で上書きされてしまうため）。'
                      : '写真のOCR読み取りに成功すると、指定した数値・金額項目に読み取り結果を自動入力します（未入力のときのみ）。人が値を修正した後は上書きされません。'}
                  </p>
                </div>
              </div>
            )}

            {isFull && draft.value_kind === 'money' && (
              <div className="space-y-3 rounded border border-surface-border p-3 bg-surface-sunken">
                {draft.catalogTaxBasis ? (
                  <p className="text-sm text-text-body">
                    税区分: {draft.catalogTaxBasis === 'tax_included' ? '税込' : '税抜'}（カタログで固定）
                  </p>
                ) : (
                <div>
                  <label className="block text-sm text-text-sub mb-1">税区分</label>
                  <Select value={draft.taxBasis}
                    onValueChange={(v) => setDraft({ ...draft, taxBasis: v as Draft['taxBasis'], reducedRatePortionFieldId: v === 'tax_included' ? draft.reducedRatePortionFieldId : '' })}>
                    <SelectTrigger aria-label="税区分"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tax_excluded">税抜</SelectItem>
                      <SelectItem value="tax_included">税込</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-sub mt-1">
                    日報売上の進捗で、税込の項目は消費税率の設定を使って税抜に換算します。税区分を変えると、過去の月の換算結果も変わります。
                  </p>
                </div>
                )}
                {(draft.catalogTaxBasis ?? draft.taxBasis) === 'tax_included' && (
                  <div>
                    <label className="block text-sm text-text-sub mb-1">軽減税率対象額の項目 (任意)</label>
                    <Select value={draft.reducedRatePortionFieldId || '__none__'}
                      onValueChange={(v) => setDraft({ ...draft, reducedRatePortionFieldId: v === '__none__' ? '' : v })}>
                      <SelectTrigger aria-label="軽減税率対象額の項目"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">指定なし (全額を標準税率で換算)</SelectItem>
                        {reducedRateCandidates.map((f) => <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.label ?? f.field_code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-text-sub mt-1">
                      「うち軽減税率対象の売上（税込）」を入力する金額項目を選びます。その項目が未入力の日は、全額を標準税率で換算します。
                    </p>
                  </div>
                )}
              </div>
            )}

            {draft.value_kind === 'computed' && (
              draft.computedExpr === null && draft.computedRawFallback ? (
                <p className="text-xs text-alert-text">
                  この項目の計算式は本画面では編集できない構成 (if 分岐・比較演算子・3項以上のネスト等) のため、表示のみです。編集する場合はAPI経由で更新してください。
                </p>
              ) : (
                <div>
                  <p className="text-xs font-semibold text-text-sub mb-1">計算式</p>
                  <DrExprEditor
                    expr={draft.computedExpr ?? { kind: 'field', field_definition_id: otherFields[0]?.field_definition_id ?? '' }}
                    onChange={(expr) => setDraft({ ...draft, computedExpr: expr })}
                    fields={otherFields} lineItemFields={lineItemFields}
                  />
                </div>
              )
            )}

            <div>
              <p className="text-xs font-semibold text-text-sub mb-1">プレビュー（入力画面での見え方）</p>
              <FieldPreview draft={draft} />
            </div>

            <details className="rounded border border-surface-border p-3">
              <summary className="text-xs font-semibold text-text-sub cursor-pointer">詳細設定 (任意)</summary>
              <div className="space-y-3 mt-3">
                <div>
                  <label className="block text-sm text-text-sub mb-1">前日から引き継ぐ項目</label>
                  <Select value={draft.carryoverSourceFieldId || '__none__'} onValueChange={(v) => setDraft({ ...draft, carryoverSourceFieldId: v === '__none__' ? '' : v })}>
                    <SelectTrigger aria-label="引き継ぎ元"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">指定なし</SelectItem>
                      {carryoverCandidates.map((f) => <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.label ?? f.field_code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-text-sub mb-1">可視性クラス (任意)</label>
                  <Select value={draft.visibilityClassId || '__none__'} onValueChange={(v) => setDraft({ ...draft, visibilityClassId: v === '__none__' ? '' : v })}>
                    <SelectTrigger aria-label="可視性クラス"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">未設定（誰にも表示しません）</SelectItem>
                      {activeVisibilityClasses.map((c) => <SelectItem key={c.class_id} value={c.class_id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-sub mt-1">
                    クラスの追加・編集は「可視性クラス」タブで行います。未設定のままだと誰にも表示されません。
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-text-sub mb-1">検証ルール (JSON、任意)</label>
                  <textarea value={draft.validationJson} onChange={(e) => setDraft({ ...draft, validationJson: e.target.value })}
                    rows={3} className={`${inputCls} font-mono text-xs`} placeholder='例: {"min": 0}' />
                </div>
                {draft.id && (
                  <label className="flex items-center gap-2 text-sm text-text-body min-h-[44px]">
                    <input type="checkbox" checked={draft.status === 'active'}
                      onChange={(e) => setDraft({ ...draft, status: e.target.checked ? 'active' : 'archived' })} className="w-4 h-4" />
                    有効
                  </label>
                )}
              </div>
            </details>
          </div>
        )}
      </Modal>
    </div>
  );
}
